import { ScratchCard, SC_CONFIG } from 'scratchcard-js';
import type { GameRenderer } from '../../interfaces/game-type-plugin.interface';
import type { ScratchcardGameSettings } from '@kanek/shared-data-access';
import { ScratchcardConfigMapper } from './scratchcard-config-mapper';
import { ConfettiService, AudioService } from '@kanek/shared-ui';
import { ScratchcardSceneComponent } from '../../scenes/scratchcard-scene.component';

import { MockPrizeApiService } from '@kanek/shared-data-access';

/**
 * Prize object structure
 */
export interface Prize {
  id: string;
  name: string;
  value: string;
  type?: string;
  probability?: number;
}

/**
 * Configuration object for ScratchcardRenderer.updateConfig()
 */
export interface ScratchcardRendererConfig extends ScratchcardGameSettings {
  _previewPrizes?: Prize[];
  prizeText?: string; // Prize to display under scratcher (for Experience App - single mode)
  winningPrize?: Prize; // The pre-determined winning prize (for Experience App - grid mode)
  _onComplete?: () => void; // Optional completion callback for preview mode
}

// Constants
const DEFAULT_CONTAINER_SIZE = 768;
const DEFAULT_GRID_SPACING = 5;
const DEFAULT_PERCENT_THRESHOLD_GRID = 40;
const DEFAULT_PERCENT_THRESHOLD_SINGLE = 70;
const MAX_GRID_SIZE = 5; // 5x5 = 25 squares max
const MATCH_THREE_COUNT = 3;
const MATCH_TWO_COUNT = 2;
const MIN_GRID_SIZE_FOR_DRAMATIC_PRIZE = 9; // 3x3
const WIN_CELEBRATION_DELAY_MS = 2000;
const SOUND_VOLUME = 0.5;

// Asset paths
const ASSET_PATHS = {
  TWO_MATCH_SOUND: 'assets/sfx/scratchcard-two-match.mp3',
  WIN_SOUND: 'assets/sfx/scratchcard-win.mp3',
  DEFAULT_COVER_IMAGE: 'assets/scratchcard/kanek-scratch-cover-clean.svg',
  DEFAULT_BRUSH: '/assets/logos/kanek/kanek-logo-black.png',
} as const;

/**
 * ScratchcardRenderer
 *
 * Implements GameRenderer interface for scratchcard-js library.
 * Provides consistent rendering across Portal preview and Experience App.
 *
 * @example
 * ```typescript
 * const renderer = new ScratchcardRenderer(containerElement);
 * await renderer.updateConfig(settings);
 * ```
 */
export class ScratchcardRenderer implements GameRenderer<ScratchcardRendererConfig> {
  private scratchcardInstances: ScratchCard[] = [];
  private gridPrizes: (Prize | null)[] = [];
  private container?: HTMLElement;
  private currentSettings?: ScratchcardRendererConfig;
  private completionTriggered = false;
  private gridSize = 1;

  // Grid mode match-3 tracking
  private scratchedSquares = new Set<number>();
  private prizeScratched = new Map<string, number>(); // prizeId → count
  private winningPrize: Prize | null = null;

  // Visual feedback tracking
  private prizeIndices = new Map<string, number[]>(); // prizeId → [square indices]
  private twoMatchPrizes = new Set<string>(); // prizeIds with exactly 2 matches
  private completionDelayTimeout?: ReturnType<typeof setTimeout>;
  private confettiService?: ConfettiService;
  private audioService?: AudioService;
  private arService?: any; // ArService (using any to avoid circular dependency)
  private animationStylesInjected = false;

  // Optional event callback for game completion
  public onComplete?: () => void;

  /**
   * Set AR service for keepVisible flag during critical moments
   */
  setArService(arService: any): void {
    this.arService = arService;
  }

  /**
   * Initialize scratchcard renderer with container element
   * @param container Container element for the scratchcard
   * @param confettiService Optional confetti service for celebrations
   * @param audioService Optional audio service for sound effects
   * @param arService Optional AR service for keepVisible flag during critical moments
   */
  initialize(container: HTMLElement, confettiService?: ConfettiService, audioService?: AudioService, arService?: any): void {
    this.container = container;
    this.confettiService = confettiService;
    this.audioService = audioService;
    this.arService = arService;

    if (this.audioService) {
      console.log('[ScratchcardRenderer] 🔊 AudioService injected');
      // Preload sound effects
      this.audioService.preload(ASSET_PATHS.TWO_MATCH_SOUND);
      this.audioService.preload(ASSET_PATHS.WIN_SOUND);
    }

    // Set critical container styles for proper scratchcard interaction
    this.container.style.cssText += `
      user-select: none;
      pointer-events: auto;
      position: relative;
      overflow: hidden;
    `;
  }

  /**
   * Get the Angular Three scene component for declarative 3D rendering.
   *
   * **Phase D Architecture**: This method returns the ScratchcardSceneComponent
   * which handles all 3D rendering declaratively using Angular Three.
   *
   * Benefits:
   * - ✅ Automatic updates via Angular signals (no manual scene manipulation)
   * - ✅ Type-safe props with TypeScript
   * - ✅ Easier testing and debugging
   * - ✅ Follows Angular 19+ patterns
   *
   * Usage:
   * ```typescript
   * const SceneComponent = renderer.getSceneComponent();
   * // In template: <ngt-canvas [sceneGraph]="SceneComponent" />
   * ```
   *
   * @returns ScratchcardSceneComponent class for declarative rendering
   * @see ScratchcardSceneComponent for implementation details
   */
  getSceneComponent() {
    return ScratchcardSceneComponent;
  }

  /**
   * Update scratchcard configuration
   * Destroys existing instance and creates new one with updated settings
   */
  async updateConfig(settings: ScratchcardRendererConfig): Promise<void> {
    // Store settings - needed for when initialize() is called later
    this.currentSettings = settings;

    // Container is optional in Phase D - scene component handles its own rendering
    // Always store winningPrize immediately so it's available when rendering happens
    if (settings.winningPrize) {
      this.winningPrize = settings.winningPrize;
    }

    // If no container yet, defer full initialization until initialize() is called
    if (!this.container) {
      return;
    }

    // Reset completion flag and tracking state for new config
    this.completionTriggered = false;
    this.scratchedSquares.clear();
    this.prizeScratched.clear();

    // IMPORTANT: Preserve existing winningPrize before cleanup
    // Game-engine may have set this via Prize API before scene component calls updateConfig
    const existingWinningPrize = this.winningPrize;

    // Destroy existing scratchcard(s) - this will reset winningPrize in cleanup
    this.cleanup();

    // Get prizes first to determine winning prize
    const prizes = settings._previewPrizes || [];

    // Set winningPrize in order of priority:
    // 1. If settings provide winningPrize, use that (explicit override)
    // 2. If we had a winningPrize before cleanup, restore it (from Prize API)
    // 3. Otherwise, randomly select from prizes (preview mode)
    if (settings.winningPrize) {
      this.winningPrize = settings.winningPrize;
    } else if (existingWinningPrize) {
      this.winningPrize = existingWinningPrize;
    } else if (prizes.length > 0) {
      // Preview mode: randomly select winning prize for match-3 gameplay using crypto RNG
      this.winningPrize = prizes[MockPrizeApiService.getSecureRandomInt(0, prizes.length)];
    } else {
      this.winningPrize = null;
    }

    // Assign preview completion callback if provided
    if (settings._onComplete) {
      this.onComplete = settings._onComplete;
    }

    // Determine grid configuration
    // Grid mode: calculate grid size based on prize count (capped at 5x5 = 25 squares max)
    // Single mode is just a 1x1 grid with 1 prize (consistent layout)
    this.gridSize = settings.gridMode
      ? Math.min(MAX_GRID_SIZE, this.calculateGridSize(Math.max(1, prizes.length)))
      : 1; // Single mode = 1x1 grid
    const totalSquares = this.gridSize * this.gridSize;

    // Use containerWidth/Height from settings (defaults to match wheel)
    const containerWidth = settings.containerWidth || DEFAULT_CONTAINER_SIZE;
    const containerHeight = settings.containerHeight || DEFAULT_CONTAINER_SIZE;
    const spacing = settings.gridSpacing || DEFAULT_GRID_SPACING;

    // Prepare prizes and build tracking maps (works for both grid and single modes)
    this.gridPrizes = this.preparePrizesForMode(prizes, totalSquares, settings.gridMode || false);

    // Build prizeIndices map for visual feedback tracking (both modes)
    this.prizeIndices.clear();
    this.gridPrizes.forEach((prize, index) => {
      if (prize && prize.id) {
        const indices = this.prizeIndices.get(prize.id) || [];
        indices.push(index);
        this.prizeIndices.set(prize.id, indices);
      }
    });

    // Inject animation styles once (both modes need this)
    this.injectAnimationStyles();

    // Style container
    this.styleContainer(this.gridSize, spacing, settings);

    // Create scratchcard squares progressively to avoid blocking UI thread
    // Use requestAnimationFrame between squares to allow browser to render
    for (let i = 0; i < totalSquares; i++) {
      await this.createGridSquare(i, settings);

      // Yield to browser between squares to prevent UI blocking
      // This allows the first square to render before initializing the rest
      if (i < totalSquares - 1) {
        await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)));
      }
    }

    console.log('[ScratchcardRenderer] All squares created successfully');
  }

  /**
   * Style the container based on grid configuration
   * Container size is defined by settings.containerWidth/Height (default 768x768)
   * Grid uses CSS fractional units (fr) to auto-fill and stretch to container
   */
  private styleContainer(
    gridSize: number,
    spacing: number,
    settings: ScratchcardGameSettings
  ): void {
    if (!this.container) return;

    // Background styling
    let backgroundStyle = '';
    if (settings.cardBackgroundImage) {
      backgroundStyle = `background-image: url(${settings.cardBackgroundImage}); background-size: cover; background-position: center;`;
    } else {
      backgroundStyle = `background: ${settings.cardBackgroundColor || '#8FA5C1'};`;
    }

    // Container size from settings
    const containerWidth = settings.containerWidth || DEFAULT_CONTAINER_SIZE;
    const containerHeight = settings.containerHeight || DEFAULT_CONTAINER_SIZE;

    // Use CSS Grid with fractional units to auto-fill container
    // This allows rectangles and automatically handles aspect ratio
    this.container.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${gridSize}, 1fr);
      grid-template-rows: repeat(${gridSize}, 1fr);
      gap: ${spacing}px;
      padding: ${spacing}px;
      width: ${containerWidth}px;
      height: ${containerHeight}px;
      ${backgroundStyle}
      position: relative;
      margin: 0 auto;
      user-select: none;
      pointer-events: auto;
    `;
  }

  /**
   * Create a single scratchcard square
   * Works for both single mode (1x1 grid) and multi-grid mode
   * Cell dimensions calculated directly from container size and grid layout
   */
  private async createGridSquare(
    index: number,
    settings: ScratchcardGameSettings
  ): Promise<void> {
    if (!this.container) return;

    const prize = this.gridPrizes[index];
    const isTryAgain = prize === null;
    const isGridMode = settings.gridMode || false;

    // Calculate cell dimensions from container and grid
    const containerWidth = settings.containerWidth || DEFAULT_CONTAINER_SIZE;
    const containerHeight = settings.containerHeight || DEFAULT_CONTAINER_SIZE;
    const spacing = settings.gridSpacing || DEFAULT_GRID_SPACING;

    // Calculate actual cell size accounting for padding and gaps
    const totalHorizontalSpacing = spacing * (this.gridSize + 1); // padding + gaps
    const totalVerticalSpacing = spacing * (this.gridSize + 1);
    const width = Math.floor((containerWidth - totalHorizontalSpacing) / this.gridSize);
    const height = Math.floor((containerHeight - totalVerticalSpacing) / this.gridSize);

    // Create square container - CSS Grid handles positioning
    const squareContainer = document.createElement('div');
    squareContainer.style.cssText = `
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `;
    squareContainer.setAttribute('data-grid-index', index.toString());
    this.container.appendChild(squareContainer);

    // Determine prize display
    const prizeDisplay = isTryAgain ? 'Try Again' : (prize?.name || 'You Won!');
    const prizeValue = isTryAgain ? '🎲' : (prize?.value || 'Prize Preview');
    const prizeColor = isTryAgain ? '#888888' : (settings.prizeBackgroundColor || settings.htmlBackground || '#22C55E');
    const prizeIcon = isTryAgain ? '🎲' : '🎁';

    // Responsive font sizes based on square dimensions
    // Scale fonts proportionally to the smaller dimension (width or height)
    const baseDimension = Math.min(width, height);
    const scaleFactor = baseDimension / 200; // 200px as base reference size

    const fontSize = Math.max(10, Math.round(12 * scaleFactor));
    const iconSize = Math.max(16, Math.round(24 * scaleFactor));
    const titleSize = Math.max(10, Math.round(14 * scaleFactor));
    const valueSize = Math.max(8, Math.round(11 * scaleFactor));
    const padding = Math.max(4, Math.round(8 * scaleFactor));
    const iconMargin = Math.max(2, Math.round(6 * scaleFactor));

    // Build config
    const config: SC_CONFIG = {
      scratchType: ScratchcardConfigMapper.mapScratchType(settings.scratchType),
      containerWidth: width,
      containerHeight: height,
      htmlBackground: `<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: ${width}px; height: ${height}px; background: ${prizeColor}; color: white; font-family: Arial, sans-serif; box-sizing: border-box; user-select: none; font-size: ${fontSize}px;"><div style="font-size: ${iconSize}px; margin-bottom: ${iconMargin}px; pointer-events: none;">${prizeIcon}</div><div style="font-weight: bold; text-align: center; padding: 0 ${padding}px; margin-bottom: ${Math.round(iconMargin / 2)}px; pointer-events: none; font-size: ${titleSize}px; line-height: 1.2; text-shadow: 1px 1px 2px rgba(0,0,0,0.7);">${prizeDisplay}</div><div style="text-align: center; padding: 0 ${padding}px; opacity: 0.9; pointer-events: none; font-size: ${valueSize}px; text-shadow: 1px 1px 2px rgba(0,0,0,0.7);">${prizeValue}</div></div>`,
      imageForwardSrc: settings.imageForwardSrc?.trim() || ASSET_PATHS.DEFAULT_COVER_IMAGE,
      imageBackgroundSrc: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+',
      clearZoneRadius: isGridMode ? Math.min(20, width / 6) : (settings.clearZoneRadius || 30),
      nPoints: isGridMode ? 100 : (settings.nPoints || 100),
      pointSize: isGridMode ? Math.min(8, width / 15) : (settings.pointSize || 10),
      percentToFinish: settings.percentToFinish || (isGridMode ? DEFAULT_PERCENT_THRESHOLD_GRID : DEFAULT_PERCENT_THRESHOLD_SINGLE),
      brushSrc: settings.brushSrc?.trim() || ASSET_PATHS.DEFAULT_BRUSH,
      enabledPercentUpdate: true,
      callback: (percent?: number) => this.handleScratchProgress(index, percent, prize, isGridMode, settings),
    };

    // Create and initialize scratchcard
    const scratchcard = new ScratchCard(squareContainer, config);
    this.scratchcardInstances.push(scratchcard);

    console.log('[ScratchcardRenderer] Initializing scratchcard square', index, 'with config:', {
      scratchType: config.scratchType,
      size: `${config.containerWidth}x${config.containerHeight}`,
      imageForwardSrc: config.imageForwardSrc,
      clearZoneRadius: config.clearZoneRadius,
      percentToFinish: config.percentToFinish
    });

    await scratchcard.init();

    // Fix canvas layering
    this.fixCanvasLayeringForContainer(squareContainer);
  }

  /**
   * Fix canvas layering for a specific container
   * Extracted to reuse for both single and grid squares
   */
  private fixCanvasLayeringForContainer(container: HTMLElement): void {
    const canvas = container.querySelector('canvas');
    if (canvas) {
      (canvas as HTMLCanvasElement).style.position = 'absolute';
      (canvas as HTMLCanvasElement).style.top = '0';
      (canvas as HTMLCanvasElement).style.left = '0';
      (canvas as HTMLCanvasElement).style.zIndex = '10';
      (canvas as HTMLCanvasElement).style.pointerEvents = 'auto';
      (canvas as HTMLCanvasElement).style.cursor = 'crosshair';

      const innerDiv = container.querySelector('.sc__inner');
      if (innerDiv) {
        (innerDiv as HTMLElement).style.pointerEvents = 'none';
        (innerDiv as HTMLElement).style.position = 'relative';
        (innerDiv as HTMLElement).style.zIndex = '1';
      }
    }
  }

  /**
   * Unified scratch progress handler for both grid and single modes
   */
  private handleScratchProgress(
    index: number,
    percent: number | undefined,
    prize: Prize | null,
    isGridMode: boolean,
    settings: ScratchcardGameSettings
  ): void {
    const threshold = settings.percentToFinish || (isGridMode ? DEFAULT_PERCENT_THRESHOLD_GRID : DEFAULT_PERCENT_THRESHOLD_SINGLE);
    const effectivePercent = percent ?? 100; // Handle library quirk - treat undefined as 100%

    if (effectivePercent < threshold) {
      return; // Threshold not reached yet
    }

    if (isGridMode) {
      this.handleGridModeProgress(index, prize);
    } else {
      this.handleSingleModeProgress(index);
    }
  }

  /**
   * Handle grid mode scratch progress - track matches and check for match-3
   */
  private handleGridModeProgress(index: number, prize: Prize | null): void {
    if (this.scratchedSquares.has(index)) {
      return; // Already processed this square
    }

    this.scratchedSquares.add(index);
    const isTryAgain = prize === null;

    // Track prize count (skip "Try Again" nulls)
    if (isTryAgain || !prize?.id) {
      return;
    }

    const currentCount = this.prizeScratched.get(prize.id) || 0;
    const newCount = currentCount + 1;
    this.prizeScratched.set(prize.id, newCount);

    // Check for 2-match (anticipation pulse)
    if (newCount === MATCH_TWO_COUNT && !this.twoMatchPrizes.has(prize.id)) {
      console.log('[ScratchcardRenderer] 🎯 2-match detected! Applying anticipation pulse:', prize.name);
      this.applyTwoMatchPulse(prize.id);
    }

    // Check if 3 matching prizes (winning condition)
    if (this.winningPrize && prize.id === this.winningPrize.id && newCount === MATCH_THREE_COUNT) {
      if (!this.completionTriggered) {
        console.log('[ScratchcardRenderer] 🎉 Match-3 complete! Winning prize revealed:', this.winningPrize.name);
        this.completionTriggered = true;
        this.handleMatchThree(prize.id);
      }
    }
  }

  /**
   * Handle single mode scratch progress - trigger immediate win
   */
  private handleSingleModeProgress(index: number): void {
    if (this.completionTriggered) {
      return; // Already completed
    }

    console.log('[ScratchcardRenderer] Single mode completion threshold reached');
    this.completionTriggered = true;

    // Trigger win celebration for single square
    this.triggerWinCelebration([index]);
  }

  /**
   * Trigger win celebration - animations, confetti, sound, and delayed completion
   * @param squareIndices Array of square indices to animate
   */
  private triggerWinCelebration(squareIndices: number[]): void {
    console.log('[ScratchcardRenderer] Triggering win celebration for squares:', squareIndices);

    // Apply winning animation to all specified squares
    squareIndices.forEach(index => {
      const square = this.container?.querySelector(`[data-grid-index="${index}"]`) as HTMLElement;
      if (square) {
        square.classList.remove('two-match-pulse'); // Remove anticipation if present
        square.classList.add('winning-square');
      }
    });

    // Trigger confetti celebration
    if (this.confettiService) {
      console.log('[ScratchcardRenderer] Triggering confetti win()...');
      this.confettiService.win();
    } else {
      console.warn('[ScratchcardRenderer] Confetti service not available');
    }

    // Play celebration sound
    this.playSound(ASSET_PATHS.WIN_SOUND);

    // Delay before triggering onComplete callback
    this.completionDelayTimeout = setTimeout(() => {
      console.log('[ScratchcardRenderer] Celebration delay complete, calling onComplete');
      this.onComplete?.();
    }, WIN_CELEBRATION_DELAY_MS);
  }

  private calculateGridSize(prizeCount: number): number {
    return Math.ceil(Math.sqrt(prizeCount));
  }

  /**
   * Prepare prizes for the specified mode
   * Normalizes prize selection and sets winningPrize for both grid and single modes
   */
  private preparePrizesForMode(prizes: Prize[], totalSquares: number, isGridMode: boolean): (Prize | null)[] {
    if (isGridMode) {
      return this.preparePrizesForGrid(prizes, totalSquares);
    } else {
      // Single mode: select one random prize and set as winningPrize
      if (prizes.length === 0) {
        this.winningPrize = null;
        return [null];
      }

      const selectedPrize = prizes[MockPrizeApiService.getSecureRandomInt(0, prizes.length)];
      this.winningPrize = selectedPrize;
      console.log('[ScratchcardRenderer] Single mode - selected prize:', selectedPrize?.name);
      return [selectedPrize];
    }
  }

  /**
   * Prepare prizes for grid mode using match-3 logic:
   * - Place winning prize in exactly 3 random squares
   * - Distribute remaining prizes ensuring max 2 of any non-winning prize
   * - DRAMATIC PRIZE LOGIC: Ensure 2 squares of lowest-probability non-winning prize (for excitement)
   * - Fill remaining squares with "Try Again" nulls
   */
  private preparePrizesForGrid(prizes: Prize[], totalSquares: number): (Prize | null)[] {
    if (!this.winningPrize) {
      // Fallback: distribute prizes randomly
      const gridPrizes: (Prize | null)[] = [];
      for (let i = 0; i < prizes.length; i++) {
        gridPrizes.push(prizes[i]);
      }
      for (let i = prizes.length; i < totalSquares; i++) {
        gridPrizes.push(null);
      }
      return this.shuffleArray(gridPrizes);
    }

    const gridPrizes: (Prize | null)[] = new Array(totalSquares).fill(null);
    const usedIndices = new Set<number>();

    // Step 1: Place winning prize in squares (up to 3 for match-3, or fewer if grid is smaller)
    const winningPrizeCount = Math.min(MATCH_THREE_COUNT, totalSquares);
    console.log('[ScratchcardRenderer] Placing winning prize in', winningPrizeCount, 'squares (grid size:', totalSquares, ')');

    for (let i = 0; i < winningPrizeCount; i++) {
      let randomIndex: number;
      let attempts = 0;
      do {
        randomIndex = MockPrizeApiService.getSecureRandomInt(0, totalSquares);
        attempts++;
        if (attempts > totalSquares * 10) {
          console.error('[ScratchcardRenderer] Could not find unused square after', attempts, 'attempts');
          break;
        }
      } while (usedIndices.has(randomIndex));

      if (attempts <= totalSquares * 10) {
        gridPrizes[randomIndex] = this.winningPrize;
        usedIndices.add(randomIndex);
      }
    }

    // Step 2: Get non-winning prizes sorted by probability (lowest first = "top prize")
    const otherPrizes = prizes
      .filter(p => p.id !== this.winningPrize?.id)
      .sort((a, b) => (a.probability || 0) - (b.probability || 0));

    // Step 3: DRAMATIC PRIZE LOGIC - Ensure 2 squares of lowest-probability prize
    // Only apply if grid is 3x3 or larger (9+ squares) AND there are enough unused squares
    const prizeDistribution = new Map<string, number>();
    const remainingSquares = totalSquares - usedIndices.size;
    if (totalSquares >= MIN_GRID_SIZE_FOR_DRAMATIC_PRIZE && otherPrizes.length > 0 && remainingSquares >= MATCH_TWO_COUNT) {
      const dramaticPrize = otherPrizes[0]; // Lowest probability = "top prize"

      // Place 2 of this prize first (or fewer if not enough squares)
      const dramaticCount = Math.min(MATCH_TWO_COUNT, remainingSquares);
      for (let i = 0; i < dramaticCount; i++) {
        let randomIndex: number;
        let attempts = 0;
        do {
          randomIndex = MockPrizeApiService.getSecureRandomInt(0, totalSquares);
          attempts++;
          if (attempts > totalSquares * 10) {
            console.error('[ScratchcardRenderer] Could not find unused square for dramatic prize after', attempts, 'attempts');
            break;
          }
        } while (usedIndices.has(randomIndex));

        if (attempts <= totalSquares * 10) {
          gridPrizes[randomIndex] = dramaticPrize;
          usedIndices.add(randomIndex);
        }
      }
      prizeDistribution.set(dramaticPrize.id, dramaticCount);
    }

    // Step 4: Distribute other prizes (max 2 of each)
    const availableSquares = Array.from({ length: totalSquares }, (_, i) => i)
      .filter(i => !usedIndices.has(i));

    for (const square of availableSquares) {
      if (otherPrizes.length === 0) {
        // No more prizes, leave as "Try Again" (null)
        break;
      }

      // Find prizes that haven't been used 2 times yet
      const availablePrizes = otherPrizes.filter(p => {
        const count = prizeDistribution.get(p.id) || 0;
        return count < MATCH_TWO_COUNT;
      });

      if (availablePrizes.length === 0) {
        // All non-winning prizes used max 2 times, fill rest with "Try Again"
        break;
      }

      // Pick random available prize using crypto RNG
      const randomPrize = availablePrizes[MockPrizeApiService.getSecureRandomInt(0, availablePrizes.length)];
      gridPrizes[square] = randomPrize;
      prizeDistribution.set(randomPrize.id, (prizeDistribution.get(randomPrize.id) || 0) + 1);
    }

    console.log('[ScratchcardRenderer] Grid prizes prepared:', {
      totalSquares,
      winningPrize: this.winningPrize?.name,
      winningPrizeCount: gridPrizes.filter(p => p?.id === this.winningPrize?.id).length,
      distribution: Array.from(prizeDistribution.entries()).map(([id, count]) => ({
        prize: prizes.find(p => p.id === id)?.name,
        count
      })),
      tryAgainCount: gridPrizes.filter(p => p === null).length
    });

    // No shuffle - prizes are already randomly placed
    return gridPrizes;
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = MockPrizeApiService.getSecureRandomInt(0, i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Play the scratchcard game
   * Calls Prize API to determine winning prize for Experience App
   * For preview mode without getPrizeIndex, returns mock result
   */
  async play(options?: { getPrizeIndex?: () => Promise<number> }): Promise<{ isWinner: boolean; prize?: { id: string; name: string; type: string }; metadata?: Record<string, unknown> }> {
    // If getPrizeIndex callback provided (Experience App), call it to get winning prize
    if (options?.getPrizeIndex && this.gridPrizes.length > 0) {
      try {
        const prizeIndex = await options.getPrizeIndex();

        // Set winning prize from the grid prizes array
        const prize = this.gridPrizes[prizeIndex];
        if (prize) {
          this.winningPrize = prize;

          return {
            isWinner: true,
            prize: {
              id: prize.id,
              name: prize.name,
              type: prize.type || 'prize'
            },
            metadata: {
              prizeIndex,
              mode: 'experience'
            }
          };
        }
      } catch (error) {
        console.error('[ScratchcardRenderer] Failed to get prize index:', error);
      }
    }

    // Preview mode or fallback: user can manually scratch to test
    return {
      isWinner: false,
      metadata: {
        mode: 'preview',
        message: 'Scratch manually to test'
      }
    };
  }

  /**
   * Inject CSS animation styles for visual feedback
   * Only injects once to avoid duplicate style tags
   */
  private injectAnimationStyles(): void {
    if (this.animationStylesInjected) return;

    const styleId = 'scratchcard-visual-feedback-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes anticipationPulse {
        0%, 100% {
          box-shadow: 0 0 0 rgba(34, 197, 94, 0);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 0 20px rgba(34, 197, 94, 0.6),
                      0 0 40px rgba(34, 197, 94, 0.4),
                      inset 0 0 20px rgba(34, 197, 94, 0.2);
          transform: scale(1.02);
        }
      }

      @keyframes prizePulse {
        0%, 100% {
          box-shadow: 0 0 0 rgba(234, 179, 8, 0);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 0 30px rgba(234, 179, 8, 0.8),
                      0 0 60px rgba(234, 179, 8, 0.6),
                      inset 0 0 30px rgba(234, 179, 8, 0.3);
          transform: scale(1.05);
        }
      }

      .two-match-pulse {
        animation: anticipationPulse 1.2s ease-in-out infinite !important;
        border: 3px solid rgba(34, 197, 94, 0.8) !important;
        z-index: 100 !important;
      }

      .winning-square {
        animation: prizePulse 0.5s ease-in-out infinite !important;
        border: 4px solid rgba(234, 179, 8, 1) !important;
        z-index: 200 !important;
      }
    `;

    document.head.appendChild(style);
    this.animationStylesInjected = true;
  }

  /**
   * Apply 2-match pulse animation to squares with matching prize
   * Only applies to SCRATCHED squares to maintain drama/suspense
   */
  private applyTwoMatchPulse(prizeId: string): void {
    const indices = this.prizeIndices.get(prizeId);
    if (!indices) return;

    // Only apply to scratched squares (filter by scratchedSquares Set)
    const scratchedIndices = indices.filter(index => this.scratchedSquares.has(index));

    console.log('[ScratchcardRenderer] Applying 2-match pulse to prize:', prizeId, 'scratched squares:', scratchedIndices, 'total squares:', indices);

    // Play subtle tension sound
    this.playSound(ASSET_PATHS.TWO_MATCH_SOUND);

    // Add pulse animation ONLY to scratched squares
    scratchedIndices.forEach(index => {
      const square = this.container?.querySelector(`[data-grid-index="${index}"]`) as HTMLElement;
      if (square) {
        square.classList.add('two-match-pulse');
      }
    });

    this.twoMatchPrizes.add(prizeId);
  }

  /**
   * Handle 3-match completion (winning condition)
   */
  private handleMatchThree(prizeId: string): void {
    const indices = this.prizeIndices.get(prizeId);
    if (!indices) return;

    console.log('[ScratchcardRenderer] Match-3 complete! Prize:', prizeId);

    // Remove all 2-match animations to focus on the winning animation
    this.removeAllTwoMatchAnimations();

    // Keep scene visible during celebration - AR tracking no longer critical
    if (this.arService) {
      this.arService.keepVisible = true;
    }

    // Trigger win celebration for all matching squares
    this.triggerWinCelebration(indices);
  }

  /**
   * Remove all 2-match pulse animations from the grid
   */
  private removeAllTwoMatchAnimations(): void {
    const allSquares = this.container?.querySelectorAll('[data-grid-index]') as NodeListOf<HTMLElement>;
    allSquares?.forEach(square => {
      square.classList.remove('two-match-pulse');
    });
    console.log('[ScratchcardRenderer] Removed all 2-match animations to focus on winning squares');
  }

  /**
   * Play sound effect using AudioService
   */
  private playSound(src: string): void {
    this.audioService?.play(src, { volume: SOUND_VOLUME });
  }

  /**
   * Clean up scratchcard instance(s)
   */
  private cleanup(): void {
    // Clear completion delay timeout
    if (this.completionDelayTimeout) {
      clearTimeout(this.completionDelayTimeout);
      this.completionDelayTimeout = undefined;
    }

    // Clean up all instances
    this.scratchcardInstances.forEach((instance, index) => {
      try {
        // ScratchCard library doesn't expose clear method in types, but it exists
        const scratchcardWithClear = instance as unknown as { clear?: () => void };
        if (typeof scratchcardWithClear.clear === 'function') {
          scratchcardWithClear.clear();
        }
      } catch (error) {
        console.warn(`Error clearing scratchcard instance ${index}:`, error);
      }
    });

    this.scratchcardInstances = [];
    this.gridPrizes = [];

    // Reset tracking state
    this.scratchedSquares.clear();
    this.prizeScratched.clear();
    this.prizeIndices.clear();
    this.twoMatchPrizes.clear();
    this.winningPrize = null;
    this.animationStylesInjected = false;

    // Clear container HTML
    if (this.container) {
      this.container.innerHTML = '';
    }
  }

  /**
   * Destroy scratchcard instance and clean up
   */
  destroy(): void {
    this.cleanup();
    this.container = undefined;
    this.currentSettings = undefined;
    this.completionTriggered = false;
  }

  /**
   * Reset the scratchcard to a fresh, unscratched state with a newly selected
   * random winning prize.
   *
   * Destroys existing ScratchCard instances, clears all scratch-tracking state,
   * then re-runs `updateConfig()` to create a fresh card.
   *
   * Preview-only: called from GameEngineComponent.resetGame() guarded by previewMode.
   * Does NOT call the Prize API — picks a new winner via MockPrizeApiService.
   */
  async reset(): Promise<void> {
    if (!this.currentSettings) return;

    // Null out the currently stored winning prize so updateConfig() re-randomises it.
    // (updateConfig preserves the existing prize unless explicitly cleared.)
    this.winningPrize = null;
    this.completionTriggered = false;

    // Deliberately omit winningPrize from the settings passed to updateConfig.
    // If we pass currentSettings as-is, the priority-1 branch in updateConfig
    // (`if (settings.winningPrize)`) would immediately restore the old winner,
    // defeating the null we just set above. Stripping it here forces the
    // random-selection fallback (priority-3) to pick a fresh winner.
    const { winningPrize: _removed, ...settingsWithoutWinner } = this.currentSettings;
    await this.updateConfig(settingsWithoutWinner as ScratchcardRendererConfig);
  }

  /**
   * Get the native scratchcard instances (if needed for advanced features)
   */
  getNativeInstances(): ScratchCard[] {
    return this.scratchcardInstances;
  }
}
